<template>
  <div class="max-w-5xl mx-auto">
    <Head :title="form.name" />

    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center gap-4 text-sm text-gray-500 mb-2">
        <button class="hover:text-orange-500 transition-colors" @click="back">
          <i class="fa fa-arrow-left" />
        </button>
        <Link href="/clients" class="hover:text-orange-500 transition-colors">Clientes</Link>
        <span class="text-gray-300">/</span>
        <span class="text-gray-900 font-medium">{{ form.name }}</span>
      </div>
      
      <div class="flex items-center gap-3">
        <h1 class="text-2xl font-bold text-gray-900">Editar Cliente</h1>
        <span class="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
          Saldo: {{ formatCurrency(form.balance) }}
        </span>
      </div>
      <p class="text-gray-500 mt-1">Actualice la información del cliente y revise sus órdenes pendientes.</p>
    </div>

    <!-- Orders List (Collapsible or just standard card) -->
    <div v-if="orders.length > 0" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
      <div class="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <h2 class="text-gray-800 font-bold flex items-center gap-2">
          <i class="fa fa-shopping-bag text-orange-500" />
          Órdenes en proceso
        </h2>
        <span class="text-xs font-bold text-gray-500 bg-white border border-gray-200 px-2 py-1 rounded-md">{{ orders.length }} órdenes</span>
      </div>
      
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left bg-gray-50/50">
              <th class="px-6 py-4 font-bold text-xs text-gray-400 uppercase tracking-wider">#</th>
              <th class="px-6 py-4 font-bold text-xs text-gray-400 uppercase tracking-wider">Fecha</th>
              <th class="px-6 py-4 font-bold text-xs text-gray-400 uppercase tracking-wider">Estado</th>
              <th class="px-6 py-4 font-bold text-xs text-gray-400 uppercase tracking-wider">Observación</th>
              <th class="px-6 py-4 font-bold text-xs text-gray-400 uppercase tracking-wider">Total</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="order in orders" 
              :key="order.id" 
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4">
                <Link :href="`/orders/${order.id}`" class="text-blue-600 font-medium hover:underline">
                  {{ order.number_order || order.id }}
                </Link>
              </td>
              <td class="px-6 py-4 text-sm text-gray-600">{{ order.date }}</td>
              <td class="px-6 py-4">
                <span
                  :class="[
                    'px-2 py-1 rounded text-xs font-bold',
                    getOrderStateClass(order.order_state)
                  ]"
                >
                  {{ getOrderStateLabel(order.order_state) }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{{ order.comentary || '-' }}</td>
              <td class="px-6 py-4 text-sm font-bold text-gray-900">{{ formatCurrency(order.total) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Update Form -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="px-8 py-6 border-b border-gray-100">
        <h2 class="text-lg font-bold text-gray-900">Información del Cliente</h2>
      </div>
      
      <form @submit.prevent="update">
        <div class="p-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <!-- Empresa -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Empresa</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-building text-gray-400" />
                </div>
                <input 
                  v-model="form.companyName"
                  type="text" 
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.companyName}"
                />
              </div>
              <p v-if="form.errors.companyName" class="text-red-500 text-xs mt-1">{{ form.errors.companyName }}</p>
            </div>

            <!-- Nombre -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Nombre y Apellido</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-user text-gray-400" />
                </div>
                <input 
                  v-model="form.name"
                  type="text" 
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.name}"
                />
              </div>
              <p v-if="form.errors.name" class="text-red-500 text-xs mt-1">{{ form.errors.name }}</p>
            </div>

            <!-- Email -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Correo electrónico</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-envelope text-gray-400" />
                </div>
                <input 
                  v-model="form.email"
                  type="email" 
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.email}"
                />
              </div>
              <p v-if="form.errors.email" class="text-red-500 text-xs mt-1">{{ form.errors.email }}</p>
            </div>

            <!-- Identificación -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Alias o Cédula</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-id-card text-gray-400" />
                </div>
                <input 
                  v-model="form.identification"
                  type="text" 
                  maxlength="15"
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.identification || customErrors.identification}"
                  placeholder="Ingrese identificación..."
                  @keypress="onlyNumbers($event, 'identification')"
                  @input="onNumericInput('identification', $event)"
                />
              </div>
              <p v-if="form.errors.identification" class="text-red-500 text-xs mt-1">{{ form.errors.identification }}</p>
              <p v-if="customErrors.identification" class="text-red-500 text-xs mt-1">{{ customErrors.identification }}</p>
            </div>

            <!-- Días de Crédito -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Días de Crédito</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-calendar-alt text-gray-400" />
                </div>
                <input 
                  v-model="form.credit_days"
                  type="number" 
                  min="0"
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.credit_days}"
                  placeholder="Ej: 30"
                />
              </div>
              <p v-if="form.errors.credit_days" class="text-red-500 text-xs mt-1">{{ form.errors.credit_days }}</p>
            </div>

            <!-- Teléfono -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Teléfono</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-phone text-gray-400" />
                </div>
                <input 
                  v-model="form.phone"
                  type="tel" 
                  maxlength="15"
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.phone || customErrors.phone}"
                  placeholder="Ingrese número telefónico..."
                  @keypress="onlyNumbers($event, 'phone')"
                  @input="onNumericInput('phone', $event)"
                />
              </div>
              <p v-if="form.errors.phone" class="text-red-500 text-xs mt-1">{{ form.errors.phone }}</p>
              <p v-if="customErrors.phone" class="text-red-500 text-xs mt-1">{{ customErrors.phone }}</p>
            </div>

            <!-- Dirección -->
            <div class="space-y-1.5">
              <label class="block text-sm font-medium text-gray-700">Dirección</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-map-marker-alt text-gray-400" />
                </div>
                <input 
                  v-model="form.address"
                  type="text" 
                  class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors text-sm"
                  :class="{'border-red-300': form.errors.address}"
                />
              </div>
              <p v-if="form.errors.address" class="text-red-500 text-xs mt-1">{{ form.errors.address }}</p>
            </div>

            <!-- Tipo de Cliente -->
            <div class="space-y-1.5 md:col-span-2 lg:col-span-1">
              <label class="block text-sm font-medium text-gray-700">Tipo de cliente</label>
              <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i class="fa fa-shapes text-gray-400" />
                </div>
                <select 
                  v-model="form.typeClient" 
                  class="block w-full pl-10 pr-10 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 text-gray-700 text-sm appearance-none"
                  :class="{'border-red-300': form.errors.typeClient}"
                >
                  <option value="" disabled>Seleccionar tipo...</option>
                  <option value="freelancer">Freelancer</option>
                  <option value="spot">SPOT</option>
                  <option value="a">A</option>
                  <option value="b">B</option>
                  <option value="c">C</option>
                </select>
                <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <i class="fa fa-chevron-down text-gray-400 text-xs" />
                </div>
              </div>
              <p v-if="form.errors.typeClient" class="text-red-500 text-xs mt-1">{{ form.errors.typeClient }}</p>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-end gap-3">
          <button 
            type="button" 
            class="px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 font-medium rounded-lg transition-colors text-sm"
            @click="destroy"
          >
            <i class="fa fa-trash mr-1" /> Eliminar
          </button>
          
          <loading-button 
            :loading="form.processing" 
            class="px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors text-sm flex items-center gap-2 shadow-sm"
            type="submit"
          >
            <i class="fa fa-save" />
            Actualizar cliente
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    TrashedMessage,
  },
  layout: Layout,
  props: {
    client: Object,
    orders: Array,
  },
  remember: 'form',
  data() {
    return {
      customErrors: {
        identification: '',
        phone: '',
      },
      form: this.$inertia.form({
        name: this.client.name,
        email: this.client.email,
        phone: this.client.phone,
        identification: this.client.identification,
        companyName: this.client.companyName,
        address: this.client.address,
        balance: this.client.balance,
        typeClient: this.client.typeClient,
        credit_days: this.client.credit_days || 0,
      }),
    }
  },
  methods: {
    back() {
      window.history.back()
    },
    onlyNumbers(event, field) {
      const charCode = event.which ? event.which : event.keyCode
      // Only allow 0-9 (ASCII 48 to 57)
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault()
        this.customErrors[field] = 'Solo se permiten números en este campo'
        setTimeout(() => {
          if (this.customErrors[field] === 'Solo se permiten números en este campo') {
            this.customErrors[field] = ''
          }
        }, 3000)
      }
    },
    onNumericInput(field, event) {
      let val = event.target.value
      const max = 15
      if (/[^\d]/.test(val)) {
        this.customErrors[field] = 'Solo se permiten números en este campo'
        val = val.replace(/\D/g, '')
      } else {
        this.customErrors[field] = ''
      }
      if (val.length > max) {
        val = val.slice(0, max)
        this.customErrors[field] = `Máximo ${max} dígitos permitidos`
      }
      this.form[field] = val
    },
    update() {
      let hasError = false
      if (!this.form.identification || /[^\d]/.test(this.form.identification)) {
        this.customErrors.identification = 'Solo se permiten números en este campo'
        hasError = true
      }
      if (this.form.phone && /[^\d]/.test(this.form.phone)) {
        this.customErrors.phone = 'Solo se permiten números en este campo'
        hasError = true
      }
      if (hasError) return

      this.form.put(`/clients/${this.client.id}`)
    },
    destroy() {
      if (confirm('¿Está seguro que desea eliminar este cliente?')) {
        this.$inertia.delete(`/clients/${this.client.id}`)
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
      }).format(value)
    },
    getOrderStateLabel(state) {
      const labels = {
        cancel: 'ANULADA',
        request: 'PENDIENTE', 
        finish: 'FINALIZADA',
        pay: 'PAGADA',
        estimate: 'PRESUPUESTO',
      }
      return labels[state] || state
    },
    getOrderStateClass(state) {
      switch(state) {
      case 'estimate': return 'bg-yellow-100 text-yellow-800'
      case 'cancel': return 'bg-red-100 text-red-800'
      case 'request': return 'bg-blue-100 text-blue-800'
      case 'finish': return 'bg-green-100 text-green-800'
      case 'pay': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
      }
    },
  },
}
</script>
